import React, { useCallback, useEffect, useState, useMemo } from 'react';
import '../admin.css';
import { Card, Typography, InputNumber, Button, message, Space, Alert, Radio } from 'antd';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;

const Settings = () => {
  // Data Retention State
  const RETENTION_LIMITS = useMemo(() => ({ min: 7, max: 365, default: 30 }), []);
  const [retentionDays, setRetentionDays] = useState(RETENTION_LIMITS.default);
  const [retentionInitialDays, setRetentionInitialDays] = useState(RETENTION_LIMITS.default);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [hasBusinessAccount, setHasBusinessAccount] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);

  // Theme Preference State
  const [theme, setTheme] = useState('light');
  const [themeLoading, setThemeLoading] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);

  // Determine if user is on free plan
  const isFreePlan = useMemo(() => {
    return !currentPlan || currentPlan.plan_name?.toLowerCase() === 'free';
  }, [currentPlan]);

  const minRetention = isFreePlan ? 7 : RETENTION_LIMITS.min;
  const maxRetention = isFreePlan ? 7 : RETENTION_LIMITS.max;
  const defaultRetention = isFreePlan ? 7 : RETENTION_LIMITS.default;

  // Load current plan
  const loadCurrentPlan = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/current-plan`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json().catch(() => (null));
      if (!res.ok) return;
      
      setCurrentPlan(body);
      setHasBusinessAccount(!!body && body.plan_name !== 'free');
    } catch (err) {
      console.error('Error loading current plan:', err);
    }
  }, []);

  // Load retention settings
  const loadRetentionSettings = useCallback(async () => {
    setRetentionLoading(true);
    try {
      const res = await fetch(`${apiBase}/my/retention-settings`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json().catch(() => ({}));
      
      if (!res.ok) throw new Error('Failed to load retention settings');
      
      const value = hasBusinessAccount && body.data_retention_days != null
        ? (body.data_retention_days ?? RETENTION_LIMITS.default)
        : RETENTION_LIMITS.default;
      
      setRetentionDays(value);
      setRetentionInitialDays(value);
    } catch (err) {
      console.error('Error loading retention settings:', err);
      setRetentionDays(defaultRetention);
      setRetentionInitialDays(defaultRetention);
    } finally {
      setRetentionLoading(false);
    }
  }, [RETENTION_LIMITS, defaultRetention, hasBusinessAccount]);

  // Apply theme to document with ResizeObserver error handling
  const applyTheme = useCallback((selectedTheme) => {
    try {
      // Use requestAnimationFrame to prevent layout thrashing
      requestAnimationFrame(() => {
        if (selectedTheme === 'dark') {
          document.body.classList.add('dark-mode');
          document.body.classList.remove('light-mode');
        } else {
          document.body.classList.add('light-mode');
          document.body.classList.remove('dark-mode');
        }
      });
    } catch (error) {
      console.warn('Theme application error:', error);
    }
  }, []);

  // Load theme preference
  const loadThemePreference = useCallback(async () => {
    setThemeLoading(true);
    try {
      const res = await fetch(`${apiBase}/my/theme-preference`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json().catch(() => ({}));
      
      if (res.ok && body.theme) {
        setTheme(body.theme);
        applyTheme(body.theme);
      } else {
        // Default to light mode
        setTheme('light');
        applyTheme('light');
      }
    } catch (err) {
      console.error('Error loading theme preference:', err);
      setTheme('light');
      applyTheme('light');
    } finally {
      setThemeLoading(false);
    }
  }, [applyTheme]);

  useEffect(() => {
    loadCurrentPlan();
  }, [loadCurrentPlan]);

  useEffect(() => {
    loadRetentionSettings();
  }, [loadRetentionSettings]);

  useEffect(() => {
    loadThemePreference();
  }, [loadThemePreference]);

  // Handle retention save
  const handleRetentionSave = useCallback(async () => {
    if (isFreePlan) {
      message.info('Free plan has a fixed 7-day retention. Upgrade to extend your retention window.');
      return;
    }

    const value = Number.parseInt(retentionDays, 10);
    if (Number.isNaN(value) || value < 1) {
      message.error('Enter a valid retention period in days');
      return;
    }

    if (value < minRetention || value > maxRetention) {
      message.error(`Retention must be between ${minRetention} and ${maxRetention} days`);
      return;
    }

    setRetentionSaving(true);
    try {
      const res = await fetch(`${apiBase}/my/retention-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ data_retention_days: value })
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(body.error || 'Failed to update retention settings');
      
      message.success('Data retention preference saved');
      setRetentionInitialDays(value);
      setRetentionDays(value);
      setHasBusinessAccount(true);
    } catch (err) {
      message.error(err?.message || 'Failed to save retention settings');
    } finally {
      setRetentionSaving(false);
    }
  }, [retentionDays, isFreePlan, minRetention, maxRetention]);

  const handleRetentionDefault = useCallback(() => {
    setRetentionDays(defaultRetention);
  }, [defaultRetention]);

  // Handle theme change
  const handleThemeChange = useCallback(async (e) => {
    const newTheme = e.target.value;
    setThemeSaving(true);
    try {
      const res = await fetch(`${apiBase}/my/theme-preference`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ theme: newTheme })
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(body.error || 'Failed to update theme preference');
      
      setTheme(newTheme);
      applyTheme(newTheme);
      message.success(`Theme changed to ${newTheme} mode`);
    } catch (err) {
      message.error(err?.message || 'Failed to save theme preference');
    } finally {
      setThemeSaving(false);
    }
  }, [applyTheme]);

  return (
    <div className="abo-content">
      <Title level={2} style={{ marginBottom: 16 }}>Settings</Title>
      
      {/* Theme Preference */}
      <Card 
        title="Theme Preference"
        hoverable 
        style={{ borderRadius: 14, marginBottom: 20 }} 
        bodyStyle={{ padding: 20 }}
        loading={themeLoading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <p style={{ marginBottom: 16 }}>
              Choose your preferred theme for the application. Light mode uses bright backgrounds with dark text, 
              while dark mode uses dark backgrounds with light text.
            </p>
            
            <Radio.Group 
              onChange={handleThemeChange} 
              value={theme}
              disabled={themeSaving}
              buttonStyle="solid"
            >
              <Radio.Button value="light" style={{ marginRight: 12 }}>
                ☀️ Light Mode
              </Radio.Button>
              <Radio.Button value="dark">
                🌙 Dark Mode
              </Radio.Button>
            </Radio.Group>
            
            <div style={{ fontSize: 12, color: '#666', marginTop: 16 }}>
              {theme === 'light' 
                ? '✓ Light mode active: Bright backgrounds with dark text for better readability in well-lit environments.'
                : '✓ Dark mode active: Dark backgrounds with light text to reduce eye strain in low-light conditions.'
              }
            </div>
          </div>
        </Space>
      </Card>
      
      {/* Data Retention Settings */}
      <Card 
        title="Data Retention Settings"
        hoverable 
        style={{ borderRadius: 14 }} 
        bodyStyle={{ padding: 20 }}
        loading={retentionLoading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <p style={{ marginBottom: 16 }}>
              Configure how long your analytics data is stored before automatic deletion. 
              Longer retention periods require higher-tier plans.
            </p>
            
            {isFreePlan && (
              <Alert
                message="Free Plan Limitation"
                description="Free plan accounts have a fixed 7-day data retention period. Upgrade to a paid plan to configure custom retention settings."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            
            <Space align="center" style={{ marginBottom: 16 }}>
              <span>Retention Period:</span>
              <InputNumber
                min={minRetention}
                max={maxRetention}
                value={retentionDays}
                onChange={(val) => setRetentionDays(val ?? RETENTION_LIMITS.default)}
                disabled={retentionSaving || isFreePlan}
                style={{ width: 100 }}
                addonAfter="days"
              />
              <Button 
                type="primary" 
                onClick={handleRetentionSave}
                loading={retentionSaving}
                disabled={retentionDays === retentionInitialDays || isFreePlan}
              >
                Save
              </Button>
              <Button onClick={() => loadRetentionSettings()} disabled={retentionSaving}>
                Reload
              </Button>
              <Button 
                onClick={handleRetentionDefault} 
                disabled={retentionSaving || retentionDays === defaultRetention || isFreePlan}
              >
                Reset to default ({defaultRetention})
              </Button>
            </Space>
            
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              {isFreePlan 
                ? 'Free plan: retention is fixed at 7 days. Upgrade to extend retention.'
                : `Allowed range: ${minRetention}-${maxRetention} days. Current saved value: ${retentionInitialDays} days.`
              }
            </div>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Settings;
